import React from 'react';
import {
    Card,
    Typography,
    Box,
    Grid,
    CircularProgress,
    Paper,
    Chip
} from '@mui/material';

// Import all chart components (remaining charts only)
import WasteCategoryChart from '../charts/WasteCategoryChart';
import ClassificationActivityChart from '../charts/ClassificationActivityChart';
import SystemPerformanceChart from '../charts/SystemPerformanceChart';
import FeedbackAnalyticsChart from '../charts/FeedbackAnalyticsChart';

const ChartsAnalytics = ({ realTimeData, loading }) => {
    // Extract category data with proper percentage calculations
    const categoryData = realTimeData?.classificationMetrics?.topCategories || [];

    if (loading) {
        return (
            <Grid container spacing={3}>
                {[1, 2, 3, 4].map((i) => (
                    <Grid item xs={12} md={6} key={i}>
                        <Card elevation={3} sx={{ borderRadius: 3, p: 3, textAlign: 'center', height: 400 }}>
                            <CircularProgress sx={{ mb: 2 }} />
                            <Typography>Loading chart data...</Typography>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        );
    }

    return (
        <Grid container spacing={3}>
            {/* Waste Category Distribution - Fixed percentages */}
            <Grid item xs={12} lg={6}>
                <WasteCategoryChart
                    categories={categoryData}
                    loading={loading}
                />
            </Grid>

            {/* Spring Boot Actuator System Performance - PURE ACTUATOR DATA */}
            <Grid item xs={12} lg={6}>
                <SystemPerformanceChart
                    systemHealth={realTimeData?.systemHealth}
                    loading={loading}
                />
            </Grid>

            {/* Weekly Classification Activity - Real data */}
            <Grid item xs={12} lg={6}>
                <ClassificationActivityChart
                    classificationMetrics={realTimeData?.classificationMetrics}
                    loading={loading}
                />
            </Grid>

            {/* Comprehensive Feedback Analytics */}
            <Grid item xs={12} lg={6}>
                <FeedbackAnalyticsChart
                    feedbackMetrics={realTimeData?.feedbackMetrics}
                    loading={loading}
                />
            </Grid>

            {/* Championship-level Summary Statistics */}
            <Grid item xs={12}>
                <Paper elevation={2} sx={{ p: 3, borderRadius: 3, background: 'linear-gradient(135deg, #f8f9fa, #ffffff)' }}>
                    <Typography variant="h5" fontWeight={700} sx={{ mb: 3, color: '#1b5e20' }}>
                        🏆 Summary
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{
                                textAlign: 'center',
                                p: 3,
                                background: 'linear-gradient(135deg, #e3f2fd, #bbdefb)',
                                borderRadius: 3,
                                border: '2px solid #2196f3'
                            }}>
                                <Typography variant="h3" fontWeight={800} sx={{ color: '#1976d2', mb: 1 }}>
                                    {realTimeData?.userMetrics?.totalUsers || 0}
                                </Typography>
                                <Typography variant="body1" fontWeight={600} color="text.secondary">
                                    Total Platform Users
                                </Typography>
                                <Chip
                                    label={`+${realTimeData?.userMetrics?.weeklyNewUsers || 0} this week`}
                                    size="small"
                                    sx={{ mt: 1, background: '#2196f3', color: 'white' }}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{
                                textAlign: 'center',
                                p: 3,
                                background: 'linear-gradient(135deg, #e8f5e8, #c8e6c8)',
                                borderRadius: 3,
                                border: '2px solid #4caf50'
                            }}>
                                <Typography variant="h3" fontWeight={800} sx={{ color: '#388e3c', mb: 1 }}>
                                    {realTimeData?.classificationMetrics?.totalClassifications || 0}
                                </Typography>
                                <Typography variant="body1" fontWeight={600} color="text.secondary">
                                    Total Classifications
                                </Typography>
                                <Chip
                                    label={`${realTimeData?.classificationMetrics?.todayClassifications || 0} today`}
                                    size="small"
                                    sx={{ mt: 1, background: '#4caf50', color: 'white' }}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{
                                textAlign: 'center',
                                p: 3,
                                background: 'linear-gradient(135deg, #fff3e0, #ffcc02)',
                                borderRadius: 3,
                                border: '2px solid #ff9800'
                            }}>
                                <Typography variant="h3" fontWeight={800} sx={{ color: '#f57c00', mb: 1 }}>
                                    {realTimeData?.classificationMetrics?.avgAccuracy || 87}%
                                </Typography>
                                <Typography variant="body1" fontWeight={600} color="text.secondary">
                                    AI Model Accuracy
                                </Typography>
                                <Chip
                                    label="Live Performance"
                                    size="small"
                                    sx={{ mt: 1, background: '#ff9800', color: 'white' }}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{
                                textAlign: 'center',
                                p: 3,
                                background: 'linear-gradient(135deg, #f3e5f5, #e1bee7)',
                                borderRadius: 3,
                                border: '2px solid #9c27b0'
                            }}>
                                <Typography variant="h3" fontWeight={800} sx={{ color: '#7b1fa2', mb: 1 }}>
                                    {realTimeData?.systemHealth?.uptime || 99.0}%
                                </Typography>
                                <Typography variant="body1" fontWeight={600} color="text.secondary">
                                    System Uptime
                                </Typography>
                                <Chip
                                    label="Spring Actuator"
                                    size="small"
                                    sx={{ mt: 1, background: '#9c27b0', color: 'white' }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
        </Grid>
    );
};

export default ChartsAnalytics;
